import { Content, Polygon, HotSpot } from "@xnav/types/src/common";
import { EditContent, EditPolygon } from "../pages/editor";
import { calcMidPoint, scaleMarkup } from "./canvasFunctions";

export function createServerContent(markup: EditContent): Content {
	if (!markup) return markup as any;

	let cleanMarkup: any = markup;
	if (typeof cleanMarkup === 'string') {
		try {
			cleanMarkup = JSON.parse(cleanMarkup);
			if (typeof cleanMarkup === 'string') cleanMarkup = JSON.parse(cleanMarkup);
		} catch {}
	}

	// Clean out any lingering numeric character keys
	if (cleanMarkup && typeof cleanMarkup === 'object') {
		for (let i = 0; cleanMarkup[i] !== undefined; i++) {
			delete cleanMarkup[i];
		}
	}

	const clientScale = cleanMarkup.clientScale ?? 1;
	const videoScaledMarkup = scaleMarkup(clientScale, cleanMarkup);
	
	const polygons: Polygon[] = (videoScaledMarkup?.polygons || []).map((clientPolygon) => {
		const roundedPoints = (clientPolygon?.points || []).map(p => ({
			x: Math.round(p.x),
			y: Math.round(p.y)
		}));
		const cleanPolygon = { ...clientPolygon, points: roundedPoints };
		delete (cleanPolygon as any).midPoints;
		delete (cleanPolygon as any).midpoints;
		return cleanPolygon as Polygon;
	});

	const hotspots: HotSpot[] = (videoScaledMarkup?.hotSpots || []).map((clientHotspot) => {
		const roundedPoints = (clientHotspot?.points || []).map(p => ({
			x: Math.round(p.x),
			y: Math.round(p.y)
		}));
		return { ...clientHotspot, points: roundedPoints };
	});

	const payload: any = { 
		...videoScaledMarkup, 
		polygons: polygons,
		hotSpots: hotspots
	};
	// Aggressively strip out ephemeral frontend UI states before committing to AWS
	delete payload.selectedMarkup;
	delete payload.clientScale;
	for (let i = 0; payload[i] !== undefined; i++) {
		delete payload[i];
	}
	
	return payload as Content;
}

export function createClientContent(content: Content): EditContent {
	if (!content) return content as any;

	let parsedContent: any = content;
	// 1. If content is a JSON string, parse it (supporting double-stringified data)
	if (typeof parsedContent === 'string') {
		try {
			parsedContent = JSON.parse(parsedContent);
			if (typeof parsedContent === 'string') parsedContent = JSON.parse(parsedContent);
		} catch {
			return undefined as any;
		}
	}

	// 2. If content is an object with numeric string keys ('0', '1', ...) created by spreading a string
	if (parsedContent && typeof parsedContent === 'object' && parsedContent['0'] !== undefined && !parsedContent.id) {
		try {
			const chars: string[] = [];
			for (let i = 0; parsedContent[i] !== undefined; i++) {
				chars.push(parsedContent[i]);
			}
			const inner = JSON.parse(chars.join(''));
			parsedContent = {
				...inner,
				...parsedContent,
				polygons: parsedContent.polygons || inner.polygons || [],
				hotSpots: parsedContent.hotSpots || inner.hotSpots || []
			};
			for (let i = 0; parsedContent[i] !== undefined; i++) {
				delete parsedContent[i];
			}
		} catch (e) {
			console.error("Failed to repair corrupted indexed content object:", e);
		}
	}

	const editPolygons: EditPolygon[] = (parsedContent.polygons || []).map((polygon: any) => {
		const points = polygon?.points || [];
		const midPoints = points.length <= 1 ? [] : points.map((point: any, index: number) => {
			return calcMidPoint(point, points[(index + 1) % points.length]);
		});
		return { 
			...polygon, 
			points: points,
			midPoints: midPoints, 
			audios: polygon.audios ? polygon.audios : (polygon as any).audoTitle ? [(polygon as any).audoTitle] : [], 
			audoTitle: undefined 
		} as EditPolygon;
	});

	return {
		...parsedContent,
		polygons: editPolygons,
		hotSpots: parsedContent.hotSpots || [],
		clientScale: 1
	};
}

export function validateNameisUnique(name: string, names: string[]): boolean {
	return names.findIndex(n => n === name) < 0;
}

export function updateContentItemInTree(
	tree: any[],
	targetId: string,
	updates: Record<string, any>
): any[] {
	return tree.map(sec => {
		if (sec.id === targetId) {
			return { ...sec, ...updates };
		}
		if (sec.contents) {
			const updatedContents = sec.contents.map((item: any) => {
				if (item.id === targetId) {
					return { ...item, ...updates };
				}
				if (item.type === 'section') {
					const [updatedSub] = updateContentItemInTree([item], targetId, updates);
					return updatedSub;
				}
				return item;
			});
			return { ...sec, contents: updatedContents };
		}
		return sec;
	});
}
